import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { DynamicFormRenderer, DynamicSection } from '@/components/DynamicFormRenderer';
import { 
  ArrowLeft, ArrowRight, Eye, CheckCircle, AlertCircle, Send, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample data for preview mode
const SAMPLE_DATA = {
  first_name: 'Sample',
  middle_name: 'Preview',
  family_name: 'Applicant',
  email: 'sample@example.com',
  father_name: 'Father Name',
  mother_name: 'Mother Name',
  ahepa_affiliation: ['AHEPA'],
  member_name: 'John Papadopoulos',
  hellenic_school: 'Yes',
  hellenic_school_name: 'Hellenic School of Ottawa',
  hellenic_school_years: '5',
  hellenic_community: 'Yes',
  hellenic_community_name: 'Maria Papadopoulos',
  greek_heritage: 'My grandparents immigrated from Greece in 1950...',
  long_term_aspirations: 'I aspire to become a software engineer and contribute to the Greek community...',
  high_school_name: 'Sample High School',
  graduation_date: '2024-06-15',
  current_institution: 'University of Ottawa',
  year_of_study: '1st Year',
  program: 'Computer Science',
  next_year_institution: '',
  awards: [
    { name: "Dean's List", date: '2024-01-15' },
    { name: 'Academic Excellence Award', date: '2023-06-20' }
  ],
  extracurricular: [
    { name: 'Greek Dance Group - Lead Dancer', date: '2021-2024' },
    { name: 'Student Council - Secretary', date: '2022-2024' }
  ],
  volunteer: [
    { name: 'Community Food Bank', date: '2022-2024' },
    { name: 'Greek Festival Volunteer', date: '2021-2024' }
  ],
  work_experience: [
    { name: 'Local Restaurant - Server', date: 'Jun 2023 - Aug 2023' }
  ]
};

const ApplicationPreview = () => {
  const { scholarshipId } = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scholarship, setScholarship] = useState(null);
  const [formData, setFormData] = useState(SAMPLE_DATA); // Now editable for testing

  // Handle field changes in preview mode
  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  useEffect(() => {
    const fetchScholarship = async () => {
      try {
        const response = await axios.get(`${API}/scholarships/${scholarshipId}`);
        setScholarship(response.data);
      } catch (error) {
        toast.error('Failed to load scholarship data');
      } finally {
        setLoading(false);
      }
    };
    fetchScholarship();
  }, [scholarshipId]);

  // Get steps from form_config
  const getSteps = () => {
    if (!scholarship?.form_config) return [];
    return scholarship.form_config
      .filter(section => section.enabled)
      .map((section, index) => ({
        id: index,
        key: section.id,
        title: section.title,
        description: section.description,
        fields: section.fields
      }));
  };

  const steps = getSteps();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentSection = steps[currentStep];
  const isSponsorSection = currentSection?.key === 'sponsor';
  const isReviewSection = currentSection?.key === 'review';

  // Reset preview test data only (does NOT affect scholarship form configuration)
  const resetTestData = () => {
    setFormData(SAMPLE_DATA);
    setCurrentStep(0);
    toast.success('Test data reset - scholarship form configuration unchanged');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Preview Banner */}
      <div className="bg-emerald-600 text-white py-2 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            <span className="font-semibold">INTERACTIVE PREVIEW</span>
            <span className="text-emerald-100">- Test all form fields and functionality</span>
          </div>
          <Button 
            onClick={resetTestData}
            variant="outline" 
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-sm"
            title="Clears test data only - does not affect your scholarship form configuration"
          >
            Reset Test Data
          </Button>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-slate-500 hover:text-greek-blue transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-heading text-lg font-bold text-slate-900">{scholarship?.name}</h1>
                <p className="text-sm text-slate-500">Application Form Preview</p>
              </div>
            </div>
            <Link to="/admin">
              <Button variant="outline" className="rounded-sm border-slate-300" data-testid="back-to-admin-btn">
                Back to Admin
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            {steps.map((step, index) => (
              <div key={step.key} className="flex items-center">
                <button
                  onClick={() => setCurrentStep(index)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    currentStep === index
                      ? 'bg-greek-blue text-white'
                      : currentStep > index
                      ? 'bg-greek-gold text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                  data-testid={`preview-step-${index}-btn`}
                >
                  {currentStep > index ? <CheckCircle className="w-4 h-4" /> : index + 1}
                </button>
                {index < steps.length - 1 && (
                  <div className={`w-12 md:w-24 h-1 mx-2 ${currentStep > index ? 'bg-greek-gold' : 'bg-slate-200'}`}></div>
                )}
              </div>
            ))}
          </div>
          <p className="text-center font-medium text-slate-700">{currentSection?.title || 'Application'}</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Sponsor Section - Special handling */}
        {isSponsorSection && (
          <div className="bg-white rounded-sm border border-slate-200 p-8 space-y-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">
              {currentSection.title}
            </h2>

            {/* Show endorser form fields if they exist */}
            {currentSection?.fields?.length > 0 && (
              <div className="space-y-6">
                <p className="text-slate-600">
                  Please provide your endorser's information below. They will receive an email with a link to complete the endorsement.
                </p>
                <div className="space-y-6">
                  {currentSection.fields.map((field) => (
                    <div key={field.id} className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      {field.type === 'select' ? (
                        <select
                          value={formData[field.id] || ''}
                          onChange={(e) => handleFieldChange(field.id, e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-greek-blue"
                        >
                          <option value="">Select {field.label}</option>
                          {field.options?.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type || 'text'}
                          value={formData[field.id] || ''}
                          onChange={(e) => handleFieldChange(field.id, e.target.value)}
                          placeholder={field.placeholder}
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-greek-blue"
                        />
                      )}
                      {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-6 bg-slate-50 rounded-sm">
              <p className="text-sm text-slate-600 mb-4">
                Once you've filled in the endorser information above, click the button below to send them the endorsement request.
              </p>

              <Button 
                onClick={() => toast.info('In the actual application, this sends an email to your endorser with a link to complete the endorsement.')}
                className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
                disabled={!formData.endorser_email || !formData.endorser_name}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Endorsement Request (Preview)
              </Button>
              
              {(!formData.endorser_email || !formData.endorser_name) && (
                <p className="text-sm text-slate-500 mt-2">
                  Please fill in the endorser name and email above first
                </p>
              )}
            </div>
          </div>
        )}

        {/* Review Section - Special handling */}
        {isReviewSection && (
          <div className="bg-white rounded-sm border border-slate-200 p-8 space-y-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">
              Review Your Application
            </h2>

            <div className="space-y-6">
              {steps.filter(s => s.key !== 'sponsor' && s.key !== 'review').map((section) => {
                const safeFields = section.fields || [];
                return (
                  <div key={section.key} className="p-4 bg-slate-50 rounded-sm">
                    <h3 className="font-semibold text-slate-800 mb-2">{section.title}</h3>
                    <div className="space-y-1 text-sm text-slate-600">
                      {safeFields.slice(0, 3).map(field => (
                        <p key={field.id}>
                          <span className="font-medium">{field.label}:</span>{' '}
                          {Array.isArray(formData[field.id]) 
                            ? formData[field.id].length + ' items'
                            : formData[field.id] || '-'}
                        </p>
                      ))}
                      {safeFields.length > 3 && (
                        <p className="text-slate-400">...and {safeFields.length - 3} more fields</p>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="p-4 bg-slate-50 rounded-sm">
                <h3 className="font-semibold text-slate-800 mb-2">Sponsor Endorsement</h3>
                <p className="text-amber-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Pending endorsement (Preview mode)
                </p>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-200">
              <Button 
                onClick={() => toast.info('In the actual application, this submits the application for review.')}
                className="w-full bg-greek-gold hover:bg-greek-gold-dark text-white rounded-sm py-6 text-lg"
              >
                Submit Application (Click to test)
              </Button>
              <p className="text-center text-sm text-slate-500 mt-4">
                Once submitted, applicants cannot edit their application.
              </p>
            </div>
          </div>
        )}

        {/* Dynamic Form Sections */}
        {!isSponsorSection && !isReviewSection && scholarship?.form_config && (
          <DynamicFormRenderer
            formConfig={scholarship.form_config.filter(s => s.id !== 'sponsor' && s.id !== 'review')}
            formData={formData}
            onFormDataChange={setFormData}
            currentSection={currentStep}
            disabled={false}
            previewMode={false}
          />
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            onClick={() => setCurrentStep(prev => Math.max(prev - 1, 0))}
            disabled={currentStep === 0}
            variant="outline"
            className="rounded-sm border-slate-300"
            data-testid="preview-prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          {currentStep < steps.length - 1 && (
            <Button
              onClick={() => setCurrentStep(prev => Math.min(prev + 1, steps.length - 1))}
              className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
              data-testid="preview-next-step-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default ApplicationPreview;
